import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertTriangle, Settings } from 'lucide-react'
import { toast } from 'sonner'
import { PermisoError } from '@/lib/permisos-guard'

import { ImpuestosStats } from '@/components/impuestos/ImpuestosStats'
import { ImpuestosTable } from '@/components/impuestos/ImpuestosTable'
import { ImpuestoDetalleDialog } from '@/components/impuestos/ImpuestoDetalleDialog'
import { GestionarCatalogoDialog } from '@/components/ui/gestionar-catalogo-dialog'
import { CrearImpuestoDialog } from '@/components/ui/crear-impuesto-dialog'
import { MarcarPresentadoDialog } from '@/core/components/calendario/MarcarPresentadoDialog'

import { useImpuestosPageData, useHuecosVencimientos, impuestosPageKeys } from '@/hooks/useImpuestosPage'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { useMarcarPresentado } from '@/hooks/useCalendario'
import { usePermissions } from '@/hooks/usePermissions'
import { useQueryClient } from '@tanstack/react-query'
import type { CatalogoConMetricas } from '@/hooks/useImpuestosPage'
import type { MarcarPresentadoData } from '@/core/components/calendario/MarcarPresentadoDialog'

export default function Impuestos() {
  const { puede, esContador } = usePermissions()
  const { catalogo, stats, isLoading } = useImpuestosPageData()
  const {
    catalogo: catalogoCompleto,
    crearImpuesto,
    isCreating,
    actualizarImpuesto,
    isUpdating,
    desactivarImpuesto,
    isDeleting,
  } = useCatalogoImpuestos()
  const { data: huecos = [] } = useHuecosVencimientos()
  const queryClient = useQueryClient()
  const marcarPresentado = useMarcarPresentado()

  // Dialog states
  const [detalleOpen, setDetalleOpen] = useState(false)
  const [impuestoSeleccionado, setImpuestoSeleccionado] = useState<CatalogoConMetricas | null>(null)
  const [gestionarOpen, setGestionarOpen] = useState(false)
  const [crearOpen, setCrearOpen] = useState(false)
  const [dialogMarcar, setDialogMarcar] = useState(false)
  const [vencimientoParaMarcar, setVencimientoParaMarcar] = useState<{
    id: string; tipo_impuesto: string; cliente_nombre?: string; periodo: string; fecha_vencimiento: string; origen_tipo?: string | null
  } | null>(null)

  const handleVerDetalle = (impuesto: CatalogoConMetricas) => {
    setImpuestoSeleccionado(impuesto)
    setDetalleOpen(true)
  }

  const handleMarcarPresentado = (vencimiento: { id: string; tipo_impuesto: string; cliente_nombre?: string; periodo: string; fecha_vencimiento: string; origen_tipo?: string | null }) => {
    setVencimientoParaMarcar(vencimiento)
    setDialogMarcar(true)
  }

  const handleConfirmarPresentado = async (id: string, datos: MarcarPresentadoData) => {
    const { adjuntos: _, ...datosPresentado } = datos

    try {
      await marcarPresentado.mutateAsync({ id, datos: datosPresentado })
      toast.success('Vencimiento marcado como presentado')
    } catch (error) {
      if (error instanceof PermisoError) {
        toast.error('No tenés permisos para marcar vencimientos como presentados')
      } else {
        toast.error('Error al marcar como presentado. Si persiste, contactá soporte.')
        console.error('[Impuestos] Error marcando presentado:', error)
      }
      throw error
    }

    // Invalidar queries de la pagina de impuestos
    queryClient.invalidateQueries({ queryKey: impuestosPageKeys.all })
  }

  // ORR-206: si el rol no ve el catálogo completo (asistente), mostrar solo los impuestos
  // que usan sus clientes delegados. clientes_asignados ya viene scopeado por la RLS de
  // impuestos_cliente (Fase 3 Tanda A).
  const catalogoVisible = puede.verCatalogoImpuestos
    ? catalogo
    : catalogo.filter((i) => i.clientes_asignados > 0)

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Impuestos"
        description="Centro de control fiscal"
      />

      <main className="flex-1 p-6 space-y-6">
        {/* Action buttons — ORR-631: con editarCatalogoImpuestos; el asistente es ver-solo (ORR-206) */}
        {puede.editarCatalogoImpuestos && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setGestionarOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Gestionar Catálogo
            </Button>
          </div>
        )}

        {/* ORR-268: impuestos con clientes que dejaron de generar vencimientos.
            Solo contador: es información operativa del estudio, no del asistente. */}
        {esContador && huecos.length > 0 && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {huecos.length === 1
                ? '1 impuesto sin vencimientos generados'
                : `${huecos.length} impuestos sin vencimientos generados`}
            </AlertTitle>
            <AlertDescription>
              Tienen clientes asignados y ninguna fecha por delante. Si alguno no presenta
              declaración —un exento o un régimen simplificado— se saca de esta lista desde el
              catálogo.
              <ul className="mt-2 space-y-1">
                {huecos.map((h) => (
                  <li key={h.impuesto_id}>
                    <span className="font-medium">{h.nombre_corto ?? h.codigo}</span>
                    {' — '}
                    {h.clientes_activos === 1 ? '1 cliente' : `${h.clientes_activos} clientes`}
                    {h.ultimo_vencimiento
                      ? `, último el ${format(parseISO(h.ultimo_vencimiento), 'dd/MM/yyyy')}`
                      : ', nunca generó ninguno'}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats cards */}
        <ImpuestosStats stats={stats} isLoading={isLoading} />

        {/* Main table */}
        <ImpuestosTable
          data={catalogoVisible}
          isLoading={isLoading}
          onVerDetalle={handleVerDetalle}
        />

      </main>

      {/* Dialog: Detalle del impuesto */}
      <ImpuestoDetalleDialog
        open={detalleOpen}
        onOpenChange={setDetalleOpen}
        impuesto={impuestoSeleccionado}
        onMarcarPresentado={handleMarcarPresentado}
      />

      {/* Dialog: Gestionar catálogo */}
      <GestionarCatalogoDialog
        open={gestionarOpen}
        onOpenChange={setGestionarOpen}
        catalogo={catalogoCompleto}
        onActualizar={async (data) => {
          await actualizarImpuesto(data)
        }}
        onDesactivar={async (id) => {
          await desactivarImpuesto(id)
        }}
        onCrearNuevo={() => {
          setGestionarOpen(false)
          setCrearOpen(true)
        }}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />

      {/* Dialog: Crear impuesto */}
      <CrearImpuestoDialog
        open={crearOpen}
        onOpenChange={setCrearOpen}
        onCrear={async (data) => {
          await crearImpuesto(data)
          setCrearOpen(false)
        }}
        isCreating={isCreating}
      />

      {/* Dialog: Marcar presentado */}
      <MarcarPresentadoDialog
        open={dialogMarcar}
        onOpenChange={setDialogMarcar}
        vencimiento={vencimientoParaMarcar}
        onConfirmar={handleConfirmarPresentado}
      />
    </div>
  )
}
